// API Service - Handles switching between HTTP and Local modes
import { PrintSlipRequest } from '@/utilities/posSlipHelper'

import { reportIcuResponse } from './icuVerificationService'
import { TransactionService } from './transactionService'

/**
 * Thrown when the local device API (localhost) is unreachable on a network
 * level (fetch fails, DNS error, connection refused, etc.).
 *
 * This is distinct from a business response - e.g. the API responding with
 * `{ success: false }` or an empty token. Network failures should trigger
 * retry-with-connectivity-page behavior, while business responses should be
 * treated as authoritative.
 */
export class DeviceApiNetworkError extends Error {
    public readonly cause?: unknown

    constructor(message: string, cause?: unknown) {
        super(message)
        this.name = 'DeviceApiNetworkError'
        this.cause = cause
    }
}

interface LegacyPrinterStatus {
    iLogicCode: number
    iPhyCode: number
}

interface PrinterStatusDetails {
    code: number
    physicalCode: number
    level: string
    message: string
    description?: string
}

interface ApiResponse {
    success: boolean
    message: string
    status?: LegacyPrinterStatus | string
    statusCode?: PrinterStatusDetails
    paperStatus?: PrinterStatusDetails
    operation?: string
    file?: string
    device_type?: string
    timestamp?: string
    stdout?: string
    stderr?: string
}

interface ActivateApiResponse {
    activated: boolean
    timer_seconds: number
    expires_at: string
}

interface DeviceTokenResponse {
    success: boolean
    token?: string
    exists?: boolean
    timestamp: string
}

interface LogResponse {
    success: boolean
    message: string
    timestamp: string
}

interface DeviceSessionResponse {
    success: boolean
    message?: string
    data: {
        sessionId: string | null
        inProgress: boolean
        startedAt?: string
        endedAt?: string
        durationMs?: number
    }
}

interface SaveTokenResponse {
    success: boolean
    message: string
    timestamp: string
}

interface DeviceCredentials {
    email: string
    device_name: string
}

interface CredentialsResponse {
    success: boolean
    credentials: DeviceCredentials | Record<string, never>
    exists: boolean
    timestamp: string
}

interface QrScannerSuccessResponse {
    success: true
    status: string
    message: string
    operation: string
    data: {
        content: string
        length: number
        scan_duration_ms: number
    }
    device_type: string
    timestamp: string
}

interface QrScannerErrorResponse {
    error: string
    message: string
    timestamp: string
}

interface PosStatusResponse {
    success: boolean
    message: string | null
    errorCode: string | null
    device: string
    device_type: string
    data:
        | {
              status: string
              terminalId: string
              configuration: {
                  timeout: number
                  currency: string
              }
          }
        | Record<string, never>
    timestamp: string
}

interface PosActivateRequest {
    amount: string
    terminalId: string
    jwt_token: string
    currency: string
    voucher_type_id: string
    depositTypeId: string
}

interface PosActivateSuccessResponse {
    success: true
    message: string
    errorCode: null
    device: string
    device_type: string
    sequenceNumber: string
    transactionNumber: string
    deviceDraftDepositId: string
    amount: string
    terminalId: string
    traceId: string
    data: {
        TerminalID: string
        MidNumber: string
        CardIssuer: string
        AID: string
        CardApplicationName: string
        CardNumber: string
        TransactionType: string
        TransactionFlag: string
        InvoiceNumber: string
        TransactionDate: string
        TransactionTime: string
        RRN: string
        ApprovalCode: string
        ResponseCode: string
        TransactionStatus: string
        Amount: string
        EMVData: string
        InstallmentsNumber: string
        SignatureLine: string
    }
    timestamp: string
}

interface PosActivateErrorResponse {
    success: false
    message: string
    errorCode: string
    device: string
    device_type: string
    data: Record<string, unknown>
    timestamp: string
}

type PosActivateResponse = PosActivateSuccessResponse | PosActivateErrorResponse

type PosSimulatorResult = {
    action: 'accept' | 'decline'
    cardType: 'VISA' | 'Mastercard'
    reason?: string
}

type QrScannerSimulatorResult = {
    // success = Submit, timeout = Timeout button, closed = window closed/blocked/aborted
    action: 'success' | 'timeout' | 'closed'
    content?: string
    reason?: string
}

type IcuCameraVerifySimulatorResult = {
    success: boolean
    age?: number
    gender?: 'male' | 'female'
    cameraIndex?: number
    cameraName?: string
    faceImage?: string
    sessionDuration?: number
    errorCode?: ICUCameraError['code']
    errorMessage?: string
    reason?: string
}

interface ICUCameraError {
    reason?: 'underage' | 'spoof_detection' | 'timeout'
    age?: number
    code?:
        | 'HTTP_ERROR'
        | 'INVALID_RESPONSE'
        | 'ALREADY_ACTIVATED'
        | 'TIMEOUT'
        | 'NO_FACE_DETECTED'
        | 'CAMERA_NOT_READY'
        | 'NETWORK_ERROR'
        | 'AUTH_ERROR'
        | 'CAMERA_ERROR'
        | 'CAMERA_OFFLINE'
        | 'INVALID_PARAMETER'
        | 'INTERNAL_ERROR'
        | 'STATUS_ERROR'
        | 'SERVICE_DISABLED'
    message: string
    category?:
        | 'timeout'
        | 'no_face'
        | 'hardware'
        | 'network'
        | 'auth'
        | 'validation'
        | 'unknown'
}

interface ICUCameraResponseEnvelope<TData> {
    success: boolean
    httpStatus?: number
    responseBody?: unknown
    data: TData | null
    message: string
    error: ICUCameraError | null
    timestamp: string
}

interface ICUCameraActivateData {
    activated: boolean
    streamUrl: string
    deviceId: string
    deviceType: string
    traceId: string
    timestamp: string
}

interface ICUCameraVerifyAgeData {
    age: number
    gender: 'male' | 'female'
    cameraIndex: number
    cameraName: string
    faceImage: string
    sessionDuration: number
    traceId: string
    timestamp: string
}

interface ICUCameraStatusData {
    activated: boolean
    connected: boolean
    streamActive: boolean
    streamUrl: string | null
    deviceInfo: {
        deviceId: string
        deviceName: string
        deviceType: string
    } | null
}

interface ICUCameraDeactivateData {
    activated: boolean
    traceId: string
    timestamp: string
}

type ICUCameraActivateResponse =
    ICUCameraResponseEnvelope<ICUCameraActivateData>
type ICUCameraVerifyAgeResponse =
    ICUCameraResponseEnvelope<ICUCameraVerifyAgeData>
type ICUCameraStatusResponse = ICUCameraResponseEnvelope<ICUCameraStatusData>
type ICUCameraDeactivateResponse =
    ICUCameraResponseEnvelope<ICUCameraDeactivateData>

class QrScannerTimeoutError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'QrScannerTimeoutError'
    }
}

type ApiMode = 'http' | 'local'

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null

const isPrinterStatusDetails = (
    value: unknown
): value is PrinterStatusDetails =>
    isRecord(value) &&
    typeof value.code === 'number' &&
    typeof value.physicalCode === 'number' &&
    typeof value.level === 'string' &&
    typeof value.message === 'string'

const normalizePrintResponse = (value: unknown): ApiResponse => {
    if (!isRecord(value)) {
        return {
            success: false,
            message: 'Print failed: Invalid printer response'
        }
    }

    const statusCode = isPrinterStatusDetails(value.statusCode)
        ? value.statusCode
        : undefined
    const paperStatus = isPrinterStatusDetails(value.paperStatus)
        ? value.paperStatus
        : undefined
    const statusCodeSuccess =
        statusCode?.level === 'success' || statusCode?.code === 0
    const paperStatusSuccess =
        paperStatus === undefined ||
        paperStatus.level === 'success' ||
        paperStatus.code === 0
    const hasSuccessfulPrinterStatus = statusCodeSuccess && paperStatusSuccess

    return {
        ...value,
        success:
            value.success === true ||
            (value.success !== false && hasSuccessfulPrinterStatus),
        message:
            typeof value.message === 'string'
                ? value.message
                : statusCode?.message ||
                  paperStatus?.message ||
                  'Print response received',
        statusCode,
        paperStatus
    }
}

const parsePrintResponseBody = (responseBody: string): unknown => {
    if (!responseBody) return {}

    try {
        return JSON.parse(responseBody)
    } catch {
        return { success: false, message: responseBody }
    }
}

class ApiService {
    private mode: ApiMode = 'http'
    private baseUrl = 'http://localhost:3001'
    private icuSimulatorPopup: Window | null = null
    private icuSimulatorId: string | null = null
    private icuSimulatorStreamUrl: string | null = null
    private icuSimulatorActivated = false
    private icuSimulatorDeviceId = 'local-icu-camera'
    private icuSimulatorReadyPromise: Promise<string> | null = null
    private icuVerifyResolvers = new Map<
        string,
        (result: IcuCameraVerifySimulatorResult) => void
    >()
    private icuSimulatorMessageHandler: ((event: MessageEvent) => void) | null =
        null

    constructor() {
        // Load mode from localStorage if available, otherwise default to http
        this.loadModeFromStorage()
    }

    /**
     * Load API mode from environment variable or localStorage, default to http if not found
     */
    private loadModeFromStorage(): void {
        // Check for environment variable override first
        const envMode = import.meta.env?.VITE_API_MODE as ApiMode
        if (envMode === 'local') {
            this.mode = 'local'
            console.log('API Service mode forced via VITE_API_MODE: local')
            return
        }

        try {
            const storedMode = localStorage.getItem('api_mode') as ApiMode
            if (storedMode && ['http', 'local'].includes(storedMode)) {
                this.mode = storedMode
                console.log(
                    `API Service mode loaded from localStorage: ${storedMode}`
                )
            } else {
                this.mode = 'http'
                console.log('API Service mode defaulted to: http')
            }
        } catch {
            this.mode = 'http'
            console.log(
                'API Service mode defaulted to: http (localStorage unavailable)'
            )
        }
    }

    /**
     * Set the API mode manually and persist to localStorage
     */
    setMode(mode: ApiMode) {
        this.mode = mode
        try {
            localStorage.setItem('api_mode', mode)
            console.log(
                `API Service mode set to: ${mode} (saved to localStorage)`
            )
        } catch {
            console.log(
                `API Service mode set to: ${mode} (localStorage save failed)`
            )
        }
    }

    /**
     * Get current API mode
     */
    getMode(): ApiMode {
        return this.mode
    }

    /**
     * Set the base URL for HTTP mode
     */
    setBaseUrl(url: string) {
        this.baseUrl = url
        console.log(`API Service base URL set to: ${url}`)
    }

    /**
     * Get current base URL
     */
    getBaseUrl(): string {
        return this.baseUrl
    }

    /**
     * Check if we should use HTTP mode
     */
    private isHttpMode(): boolean {
        return this.mode === 'http'
    }

    private getStoredSessionId(): string | undefined {
        if (typeof window === 'undefined') return undefined
        try {
            const raw = window.localStorage.getItem('vcash_order_state')
            if (!raw) return undefined
            const parsed = JSON.parse(raw) as { sessionId?: string }
            if (parsed?.sessionId && typeof parsed.sessionId === 'string') {
                const trimmed = parsed.sessionId.trim()
                return trimmed.length > 0 ? trimmed : undefined
            }
        } catch {
            // noop
        }
        return undefined
    }

    private buildHeaders(sessionId?: string): Record<string, string> {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json'
        }
        const resolvedSessionId = sessionId ?? this.getStoredSessionId()
        if (resolvedSessionId) {
            headers['X-Session-ID'] = resolvedSessionId
        }
        return headers
    }

    private createTraceId(prefix: string): string {
        const suffix =
            typeof crypto !== 'undefined' &&
            typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID().slice(0, 8)
                : Math.random().toString(36).slice(2, 10)
        return `${prefix}-${Date.now()}-${suffix}`
    }

    /**
     * HTTP implementation for saveDeviceToken
     */
    private async httpSaveDeviceToken(
        token: string,
        sessionId?: string
    ): Promise<boolean> {
        try {
            const response = await fetch(
                `${this.baseUrl}/api/v1/device/save-token`,
                {
                    method: 'POST',
                    headers: this.buildHeaders(sessionId),
                    body: JSON.stringify({ token })
                }
            )

            const data: SaveTokenResponse = await response.json()
            return data.success
        } catch (error) {
            console.error('HTTP saveDeviceToken error:', error)
            return false
        }
    }

    /**
     * HTTP implementation for getDeviceToken
     *
     * Distinguishes between network failures (fetch throws) and business
     * responses. Network failures throw `DeviceApiNetworkError` so callers can
     * retry / show a connectivity screen, instead of silently treating the
     * device as unregistered.
     */
    private async httpGetDeviceToken(sessionId?: string): Promise<string> {
        let response: Response
        try {
            response = await fetch(
                `${this.baseUrl}/api/v1/device/get-token`,
                {
                    headers: this.buildHeaders(sessionId)
                }
            )
        } catch (error) {
            console.error('HTTP getDeviceToken network error:', error)
            throw new DeviceApiNetworkError(
                'Failed to reach local device API at /api/v1/device/get-token',
                error
            )
        }

        try {
            const data: DeviceTokenResponse = await response.json()

            if (data.success && data.token) {
                return data.token
            }
            return ''
        } catch (error) {
            console.error('HTTP getDeviceToken parse error:', error)
            return ''
        }
    }

    /**
     * HTTP implementation for sendLog
     */
    private async httpSendLog(
        level: string,
        message: string,
        sessionId?: string
    ): Promise<void> {
        try {
            const response = await fetch(`${this.baseUrl}/api/v1/device/log`, {
                method: 'POST',
                headers: this.buildHeaders(sessionId),
                body: JSON.stringify({ level, message })
            })

            const data: LogResponse = await response.json()
            if (!data.success) {
                // Do nothing
            }
        } catch {
            // Do nothing
        }
    }

    /**
     * HTTP implementation for getCredentials
     */
    private async httpGetCredentials(
        sessionId?: string
    ): Promise<DeviceCredentials | null> {
        try {
            const response = await fetch(
                `${this.baseUrl}/api/v1/device/get-credentials`,
                {
                    headers: this.buildHeaders(sessionId)
                }
            )
            const data: CredentialsResponse = await response.json()

            if (
                data.success &&
                data.exists &&
                data.credentials &&
                'email' in data.credentials
            ) {
                return data.credentials as DeviceCredentials
            }
            return null
        } catch (error) {
            console.error('HTTP getCredentials error:', error)
            return null
        }
    }

    /**
     * HTTP implementation for print
     * Passes an image URL or data:image/bmp;base64,... to the localhost service.
     */
    private async httpPrint(
        imageUrl: string,
        sessionId?: string
    ): Promise<ApiResponse> {
        try {
            const response = await fetch(
                `${this.baseUrl}/api/v1/printer/print-image`,
                {
                    method: 'POST',
                    headers: this.buildHeaders(sessionId),
                    body: JSON.stringify({ file: imageUrl })
                }
            )
            const responseBody = await response.text()
            const data = parsePrintResponseBody(responseBody)
            const printResponse = normalizePrintResponse(data)

            if (!response.ok && !printResponse.success) {
                return {
                    ...printResponse,
                    success: false,
                    message: `Print failed: ${printResponse.message}`,
                    status: { iLogicCode: -1, iPhyCode: -1 }
                }
            }

            return printResponse
        } catch (error) {
            console.error('HTTP print error:', error)
            return {
                success: false,
                message: `Print failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                status: { iLogicCode: -1, iPhyCode: -1 }
            }
        }
    }

    /**
     * HTTP implementation for printSlip
     * Sends structured POS payment data to the device API, which renders a
     * 576px-wide monochrome slip and prints it via the thermal printer.
     */
    private async httpPrintSlip(
        slip: PrintSlipRequest,
        sessionId?: string
    ): Promise<ApiResponse> {
        try {
            const response = await fetch(
                `${this.baseUrl}/api/v1/printer/print-slip`,
                {
                    method: 'POST',
                    headers: this.buildHeaders(sessionId),
                    body: JSON.stringify(slip)
                }
            )
            const responseBody = await response.text()
            const data = parsePrintResponseBody(responseBody)
            const printResponse = normalizePrintResponse(data)

            if (!response.ok && !printResponse.success) {
                return {
                    ...printResponse,
                    success: false,
                    message: `Slip print failed: ${printResponse.message}`,
                    status: { iLogicCode: -1, iPhyCode: -1 }
                }
            }

            return printResponse
        } catch (error) {
            console.error('HTTP printSlip error:', error)
            return {
                success: false,
                message: `Slip print failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                status: { iLogicCode: -1, iPhyCode: -1 }
            }
        }
    }

    /**
     * HTTP implementation for activate
     */
    private async httpActivate(
        jwt: string,
        voucherTypeId: string,
        sessionId?: string
    ): Promise<ActivateApiResponse> {
        try {
            const response = await fetch(
                `${this.baseUrl}/api/v1/bill-acceptor/activate`,
                {
                    method: 'POST',
                    headers: this.buildHeaders(sessionId),
                    body: JSON.stringify({
                        token: jwt,
                        voucher_type_id: voucherTypeId
                    })
                }
            )

            const data = await response.json()
            return data
        } catch (error) {
            console.error('HTTP activate error:', error)
            return {
                activated: false,
                timer_seconds: 0,
                expires_at: ''
            }
        }
    }

    /**
     * HTTP implementation for deactivate
     */
    private async httpDeactivate(sessionId?: string): Promise<void> {
        try {
            const response = await fetch(
                `${this.baseUrl}/api/v1/bill-acceptor/deactivate`,
                {
                    method: 'POST',
                    headers: this.buildHeaders(sessionId)
                }
            )
            if (!response.ok) {
                throw new Error('Failed to deactivate')
            }
        } catch (error) {
            console.error('HTTP deactivate error:', error)
        }
    }

    /**
     * HTTP implementation for QR scanner
     */
    private async httpStartQrScanner(
        signal?: AbortSignal,
        sessionId?: string
    ): Promise<string> {
        try {
            const response = await fetch(
                `${this.baseUrl}/api/v1/qr-scanner/start`,
                {
                    method: 'POST',
                    headers: this.buildHeaders(sessionId),
                    signal
                }
            )

            if (response.ok) {
                const data: QrScannerSuccessResponse = await response.json()
                return data.data.content
            } else {
                const errorData: QrScannerErrorResponse = await response.json()

                // Map HTTP status codes to meaningful error messages
                switch (response.status) {
                    case 408:
                        throw new QrScannerTimeoutError(
                            'Scan timeout - no QR code detected within 20 seconds'
                        )
                    case 409:
                        throw new Error('Scan already in progress')
                    case 500:
                        throw new Error('QR scanner error')
                    case 503:
                        throw new Error('QR scanner service not available')
                    default:
                        throw new Error(
                            errorData.message || 'Unknown QR scanner error'
                        )
                }
            }
        } catch (error) {
            if (error instanceof Error) {
                throw error
            }
            console.error('HTTP QR scanner error:', error)
            throw new Error('Failed to start QR scanner')
        }
    }

    private async httpStopQrScanner(sessionId?: string): Promise<void> {
        try {
            const response = await fetch(
                `${this.baseUrl}/api/v1/qr-scanner/stop`,
                {
                    method: 'POST',
                    headers: this.buildHeaders(sessionId)
                }
            )
            if (!response.ok) {
                throw new Error('Failed to stop QR scanner')
            }
        } catch (error) {
            console.error('HTTP stopQR scanner error:', error)
            throw new Error('Failed to stop QR scanner')
        }
    }

    /**
     * HTTP implementation for POS status
     */
    private async httpGetPosStatus(
        sessionId?: string
    ): Promise<PosStatusResponse> {
        try {
            const headers: Record<string, string> = {}
            if (sessionId) {
                headers['X-Session-ID'] = sessionId
            }

            const response = await fetch(`${this.baseUrl}/api/v1/pos/status`, {
                headers
            })

            const data: PosStatusResponse = await response.json()
            return data
        } catch (error) {
            console.error('HTTP getPosStatus error:', error)
            return {
                success: false,
                message: `Failed to get POS status: ${error instanceof Error ? error.message : 'Unknown error'}`,
                errorCode: 'NETWORK_ERROR',
                device: 'pos',
                device_type: 'hardware',
                data: {},
                timestamp: new Date().toISOString()
            }
        }
    }

    /**
     * HTTP implementation for POS activate
     */
    private async httpActivatePos(
        request: PosActivateRequest,
        sessionId?: string
    ): Promise<PosActivateResponse> {
        try {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json'
            }
            if (sessionId) {
                headers['X-Session-ID'] = sessionId
            }

            const response = await fetch(
                `${this.baseUrl}/api/v1/pos/activate`,
                {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(request)
                }
            )

            const data: PosActivateResponse = await response.json()
            return data
        } catch (error) {
            console.error('HTTP activatePos error:', error)
            return {
                success: false,
                message: `Failed to activate POS: ${error instanceof Error ? error.message : 'Unknown error'}`,
                errorCode: 'NETWORK_ERROR',
                device: 'pos',
                device_type: 'hardware',
                data: {},
                timestamp: new Date().toISOString()
            }
        }
    }

    // One request per operation. The device service owns detection retries.
    private async httpIcuRequest<TData>(
        operation: 'activate' | 'verify-age' | 'status' | 'deactivate',
        signal?: AbortSignal,
        body?: { timeout?: number }
    ): Promise<ICUCameraResponseEnvelope<TData>> {
        let httpStatus: number | undefined
        let responseBody: unknown
        try {
            const response = await fetch(
                `${this.baseUrl}/api/v1/cam/${operation}`,
                {
                    method: operation === 'status' ? 'GET' : 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    ...(body ? { body: JSON.stringify(body) } : {}),
                    // Bound a stalled transport without overriding the vendor's scan policy.
                    signal: AbortSignal.any([
                        AbortSignal.timeout(60000),
                        ...(signal ? [signal] : [])
                    ])
                }
            )
            httpStatus = response.status
            const text = await response.text()
            responseBody = text
            try {
                responseBody = JSON.parse(text)
            } catch {
                /* Preserve non-JSON failures for diagnostics. */
            }
            const data = responseBody as ICUCameraResponseEnvelope<TData> | null
            if (!response.ok) {
                // Business rejections carry their reason and age in error,
                // not data. Preserve structured vendor failures for routing.
                if (
                    data?.success === false &&
                    data.error &&
                    typeof data.error === 'object' &&
                    !Array.isArray(data.error) &&
                    (typeof data.error.message === 'string' ||
                        (httpStatus === 403 &&
                            (data.error.reason === 'underage' ||
                                data.error.reason === 'spoof_detection')))
                ) {
                    return {
                        ...data,
                        error: {
                            ...data.error,
                            message:
                                typeof data.error.message === 'string'
                                    ? data.error.message
                                    : ''
                        },
                        httpStatus,
                        responseBody
                    }
                }
                throw new Error('HTTP_ERROR')
            }
            if (!data || typeof data.success !== 'boolean') {
                throw new Error('INVALID_RESPONSE')
            }
            return { ...data, httpStatus }
        } catch (error) {
            const code = signal?.aborted
                ? 'NETWORK_ERROR'
                : error instanceof Error && error.name === 'TimeoutError'
                  ? 'TIMEOUT'
                  : httpStatus !== undefined
                    ? httpStatus < 200 || httpStatus >= 300
                        ? 'HTTP_ERROR'
                        : 'INVALID_RESPONSE'
                    : 'NETWORK_ERROR'
            const failureBody =
                responseBody && typeof responseBody === 'object'
                    ? (responseBody as {
                          message?: unknown
                          error?: { message?: unknown } | null
                      })
                    : undefined
            const failureMessage =
                failureBody?.error?.message ?? failureBody?.message
            return {
                success: false,
                httpStatus,
                responseBody,
                data: null,
                message: `ICU ${operation} failed`,
                error: {
                    code,
                    message:
                        typeof failureMessage === 'string'
                            ? failureMessage
                            : code === 'HTTP_ERROR'
                              ? `HTTP ${httpStatus}`
                              : error instanceof Error
                                ? error.message
                                : code,
                    category: 'unknown'
                },
                timestamp: new Date().toISOString()
            }
        }
    }

    /**
     * Local implementation for saveDeviceToken
     * Persists token to localStorage
     */
    private async localSaveDeviceToken(token: string): Promise<boolean> {
        try {
            localStorage.setItem('localapi_vcash_device_token', token)
            console.log('Local saveDeviceToken: token saved to localStorage')
            return true
        } catch (error) {
            console.error('Local saveDeviceToken error:', error)
            return false
        }
    }

    /**
     * Local implementation for getDeviceToken
     * Retrieves token from localStorage
     */
    private async localGetDeviceToken(): Promise<string> {
        try {
            const token =
                localStorage.getItem('localapi_vcash_device_token') || ''
            console.log('Local getDeviceToken: retrieved from localStorage')
            return token
        } catch (error) {
            console.error('Local getDeviceToken error:', error)
            return ''
        }
    }

    /**
     * Local implementation for sendLog
     * Does nothing, just returns success
     */
    private async localSendLog(_: string, __: string): Promise<void> {
        // console.log(`Local sendLog [${level}]: ${message}`)
        // Does nothing in local mode
    }

    /**
     * Local implementation for getCredentials
     * Returns no credentials stored
     */
    private async localGetCredentials(): Promise<DeviceCredentials | null> {
        console.log('Local getCredentials: no credentials stored')
        return null
    }

    /**
     * Local implementation for print
     * Opens a visual thermal printer simulator for development
     */
    private async localPrint(imageUrl: string): Promise<ApiResponse> {
        console.log('Local print: opening printer simulator')

        const printImageUrl = imageUrl.replace('rotate=180', 'rotate=0')
        const popup = window.open(
            '',
            '_blank',
            'popup=yes,width=520,height=820,menubar=no,toolbar=no,location=no,status=no,resizable=yes'
        )

        if (!popup) {
            return {
                success: false,
                message: 'Print simulator popup was blocked',
                status: { iLogicCode: -1, iPhyCode: -1 }
            }
        }

        popup.document.write(`<!doctype html>
<html lang="en">
<head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>VCash Thermal Printer Simulator</title>
    <style>
        * { box-sizing: border-box; }
        body {
            margin: 0;
            min-height: 100vh;
            display: flex;
            align-items: flex-start;
            justify-content: center;
            background:
                radial-gradient(circle at top, rgba(96, 165, 250, 0.18), transparent 35%),
                #111827;
            font-family: Arial, sans-serif;
            color: #f9fafb;
            overflow-x: hidden;
            overflow-y: auto;
        }
        .scene {
            width: min(440px, 92vw);
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 24px 0 64px;
        }
        .printer {
            position: relative;
            z-index: 2;
            width: 100%;
            height: 210px;
            border-radius: 28px 28px 18px 18px;
            background: linear-gradient(180deg, #f3f4f6, #9ca3af);
            box-shadow: 0 24px 70px rgba(0, 0, 0, 0.5);
            border: 2px solid #e5e7eb;
            padding: 24px;
        }
        .printer::before {
            content: "";
            position: absolute;
            left: 38px;
            right: 38px;
            top: 34px;
            height: 44px;
            border-radius: 16px;
            background: #111827;
            box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.7);
        }
        .printer::after {
            content: "";
            position: absolute;
            left: 52px;
            right: 52px;
            bottom: -2px;
            height: 26px;
            border-radius: 0 0 14px 14px;
            background: #111827;
        }
        .brand {
            position: absolute;
            left: 32px;
            bottom: 36px;
            color: #111827;
            font-weight: 800;
            letter-spacing: 0.08em;
            text-transform: uppercase;
        }
        .status-light {
            position: absolute;
            right: 36px;
            bottom: 38px;
            width: 16px;
            height: 16px;
            border-radius: 999px;
            background: #22c55e;
            box-shadow: 0 0 16px #22c55e;
            animation: pulse 900ms infinite alternate;
        }
        .receipt-wrap {
            position: relative;
            width: 310px;
            min-height: 560px;
            margin-top: -8px;
            overflow: visible;
            filter: drop-shadow(0 22px 24px rgba(0, 0, 0, 0.38));
        }
        .receipt {
            width: 100%;
            min-height: 360px;
            background: white;
            color: #111827;
            border-radius: 0 0 10px 10px;
            padding: 12px;
            transform: translateY(-102%);
            animation: printOut 3.4s cubic-bezier(0.2, 0.75, 0.25, 1) forwards;
        }
        .receipt::before {
            content: "";
            display: block;
            height: 14px;
            margin: -12px -12px 10px;
            background:
                linear-gradient(135deg, transparent 8px, white 0) left,
                linear-gradient(-135deg, transparent 8px, white 0) right;
            background-size: 16px 14px;
        }
        .receipt img {
            display: block;
            width: 100%;
            height: auto;
            border: 1px solid #e5e7eb;
            background: white;
        }
        .loading {
            margin-top: 18px;
            color: #d1d5db;
            font-size: 14px;
            letter-spacing: 0.02em;
            animation: fadeOut 3.4s forwards;
        }
        .done {
            margin-top: 18px;
            color: #bbf7d0;
            font-size: 15px;
            font-weight: 700;
            opacity: 0;
            animation: fadeIn 500ms 3.1s forwards;
        }
        @keyframes printOut {
            0% { transform: translateY(-102%); }
            18% { transform: translateY(-80%); }
            38% { transform: translateY(-54%); }
            62% { transform: translateY(-28%); }
            100% { transform: translateY(0); }
        }
        @keyframes pulse {
            from { opacity: 0.45; transform: scale(0.9); }
            to { opacity: 1; transform: scale(1.08); }
        }
        @keyframes fadeOut {
            0%, 82% { opacity: 1; }
            100% { opacity: 0; }
        }
        @keyframes fadeIn {
            to { opacity: 1; }
        }
    </style>
</head>
<body>
    <main class="scene">
        <section class="printer" aria-label="Thermal printer">
            <div class="brand">VCash Printer</div>
            <div class="status-light" aria-hidden="true"></div>
        </section>
        <section class="receipt-wrap" aria-label="Printed voucher">
            <div class="receipt">
                <img id="voucher" alt="Printed voucher preview" />
            </div>
        </section>
        <div class="loading">Printing voucher...</div>
        <div class="done">Print completed</div>
    </main>
    <script>
        document.getElementById('voucher').src = ${JSON.stringify(printImageUrl)};
    </script>
</body>
</html>`)
        popup.document.close()
        popup.focus()

        return {
            success: true,
            message: 'Print successful (local mode)',
            status: { iLogicCode: 0, iPhyCode: 0 }
        }
    }

    /**
     * Local implementation for printSlip
     * Opens the thermal printer simulator and renders a basic monochrome POS
     * slip from the structured payment data. Fields print exactly as passed;
     * omitted fields do not appear.
     */
    private async localPrintSlip(slip: PrintSlipRequest): Promise<ApiResponse> {
        console.log('Local printSlip: mock POS slip print', slip)

        const escapeHtml = (value: string) =>
            value
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
        const centeredLine = (value?: string, className = '') =>
            value
                ? `<div class="slip-center ${className}">${escapeHtml(value)}</div>`
                : ''
        const row = (label: string, value?: string) =>
            value
                ? `<div class="slip-row"><span>${escapeHtml(label)}</span><span>${escapeHtml(value)}</span></div>`
                : ''

        const amountLine =
            slip.amount !== undefined
                ? [slip.amount, slip.currency].filter(Boolean).join(' ')
                : undefined
        const dateTimeLine =
            [slip.transactionDate, slip.transactionTime]
                .filter(Boolean)
                .join(' ') || undefined

        const slipHtml = [
            centeredLine(slip.companyName, 'slip-company'),
            centeredLine(slip.companyAddress),
            centeredLine(slip.companyCity),
            centeredLine(slip.locationName),
            centeredLine(slip.locationAddress),
            '<div class="slip-divider"></div>',
            row('MERCHANT ID', slip.merchantId),
            row('TERMINAL ID', slip.terminalId),
            row('RACUN BR.', slip.invoiceNumber),
            row('DATUM/VREME', dateTimeLine),
            row('KARTICA', slip.cardIssuer),
            row('BROJ KARTICE', slip.cardNumber),
            row('TIP KARTICE', slip.cardApplicationName),
            row('APPROVAL CODE', slip.approvalCode),
            row('RESPONSE CODE', slip.responseCode),
            row('RRN', slip.rrn),
            '<div class="slip-divider"></div>',
            centeredLine(slip.transactionType, 'slip-type'),
            centeredLine(amountLine, 'slip-amount'),
            centeredLine(slip.transactionStatus, 'slip-status'),
            '<div class="slip-divider"></div>',
            centeredLine(slip.copyType, 'slip-copy-type'),
            centeredLine(slip.supportPhone, 'slip-support'),
            '<div class="slip-center slip-footer">HVALA NA POVERENJU</div>'
        ]
            .filter(Boolean)
            .join('\n')

        const popup = window.open(
            '',
            '_blank',
            'popup=yes,width=520,height=820,menubar=no,toolbar=no,location=no,status=no,resizable=yes'
        )

        if (!popup) {
            return {
                success: false,
                message: 'Print simulator popup was blocked',
                status: { iLogicCode: -1, iPhyCode: -1 }
            }
        }

        popup.document.write(`<!doctype html>
<html lang="en">
<head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>VCash Thermal Printer Simulator - POS Slip</title>
    <style>
        * { box-sizing: border-box; }
        body {
            margin: 0;
            min-height: 100vh;
            display: flex;
            align-items: flex-start;
            justify-content: center;
            background:
                radial-gradient(circle at top, rgba(96, 165, 250, 0.18), transparent 35%),
                #111827;
            font-family: Arial, sans-serif;
            color: #f9fafb;
            overflow-x: hidden;
            overflow-y: auto;
        }
        .scene {
            width: min(440px, 92vw);
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 24px 0 64px;
        }
        .printer {
            position: relative;
            z-index: 2;
            width: 100%;
            height: 210px;
            border-radius: 28px 28px 18px 18px;
            background: linear-gradient(180deg, #f3f4f6, #9ca3af);
            box-shadow: 0 24px 70px rgba(0, 0, 0, 0.5);
            border: 2px solid #e5e7eb;
            padding: 24px;
        }
        .printer::before {
            content: "";
            position: absolute;
            left: 38px;
            right: 38px;
            top: 34px;
            height: 44px;
            border-radius: 16px;
            background: #111827;
            box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.7);
        }
        .printer::after {
            content: "";
            position: absolute;
            left: 52px;
            right: 52px;
            bottom: -2px;
            height: 26px;
            border-radius: 0 0 14px 14px;
            background: #111827;
        }
        .brand {
            position: absolute;
            left: 32px;
            bottom: 36px;
            color: #111827;
            font-weight: 800;
            letter-spacing: 0.08em;
            text-transform: uppercase;
        }
        .status-light {
            position: absolute;
            right: 36px;
            bottom: 38px;
            width: 16px;
            height: 16px;
            border-radius: 999px;
            background: #22c55e;
            box-shadow: 0 0 16px #22c55e;
            animation: pulse 900ms infinite alternate;
        }
        .receipt-wrap {
            position: relative;
            width: 310px;
            min-height: 460px;
            margin-top: -8px;
            overflow: visible;
            filter: drop-shadow(0 22px 24px rgba(0, 0, 0, 0.38));
        }
        .receipt {
            width: 100%;
            min-height: 320px;
            background: white;
            color: #000;
            border-radius: 0 0 10px 10px;
            padding: 18px 16px 26px;
            transform: translateY(-102%);
            animation: printOut 3.4s cubic-bezier(0.2, 0.75, 0.25, 1) forwards;
            font-family: "Courier New", Courier, monospace;
            font-size: 13px;
            line-height: 1.45;
        }
        .receipt::before {
            content: "";
            display: block;
            height: 14px;
            margin: -18px -16px 12px;
            background:
                linear-gradient(135deg, transparent 8px, white 0) left,
                linear-gradient(-135deg, transparent 8px, white 0) right;
            background-size: 16px 14px;
        }
        .slip-center { text-align: center; }
        .slip-company {
            font-weight: 700;
            font-size: 15px;
            text-transform: uppercase;
        }
        .slip-row {
            display: flex;
            justify-content: space-between;
            gap: 12px;
        }
        .slip-row span:last-child { text-align: right; word-break: break-all; }
        .slip-divider {
            border-top: 1px dashed #000;
            margin: 10px 0;
        }
        .slip-type { font-weight: 700; letter-spacing: 0.06em; }
        .slip-amount {
            font-size: 22px;
            font-weight: 800;
            margin: 4px 0;
        }
        .slip-status {
            font-weight: 800;
            font-size: 15px;
            letter-spacing: 0.08em;
        }
        .slip-footer { font-size: 11px; }
        .slip-copy-type {
            font-weight: 700;
            letter-spacing: 0.06em;
            margin-top: 6px;
        }
        .slip-support { font-size: 11px; margin-top: 4px; }
        .loading {
            margin-top: 18px;
            color: #d1d5db;
            font-size: 14px;
            letter-spacing: 0.02em;
            animation: fadeOut 3.4s forwards;
        }
        .done {
            margin-top: 18px;
            color: #bbf7d0;
            font-size: 15px;
            font-weight: 700;
            opacity: 0;
            animation: fadeIn 500ms 3.1s forwards;
        }
        @keyframes printOut {
            0% { transform: translateY(-102%); }
            18% { transform: translateY(-80%); }
            38% { transform: translateY(-54%); }
            62% { transform: translateY(-28%); }
            100% { transform: translateY(0); }
        }
        @keyframes pulse {
            from { opacity: 0.45; transform: scale(0.9); }
            to { opacity: 1; transform: scale(1.08); }
        }
        @keyframes fadeOut {
            0%, 82% { opacity: 1; }
            100% { opacity: 0; }
        }
        @keyframes fadeIn {
            to { opacity: 1; }
        }
    </style>
</head>
<body>
    <main class="scene">
        <section class="printer" aria-label="Thermal printer">
            <div class="brand">VCash Printer</div>
            <div class="status-light" aria-hidden="true"></div>
        </section>
        <section class="receipt-wrap" aria-label="Printed POS slip">
            <div class="receipt">
${slipHtml}
            </div>
        </section>
        <div class="loading">Printing POS slip...</div>
        <div class="done">Print completed</div>
    </main>
</body>
</html>`)
        popup.document.close()
        popup.focus()

        return {
            success: true,
            message: 'Slip print successful (local mode)',
            status: { iLogicCode: 0, iPhyCode: 0 }
        }
    }

    /**
     * Local implementation for activate
     * Returns success without actually activating
     */
    private async localActivate(_jwt: string): Promise<ActivateApiResponse> {
        console.log('Local activate: mock activation')
        return {
            activated: true,
            timer_seconds: 300, // 5 minutes mock timer
            expires_at: new Date(Date.now() + 300000).toISOString()
        }
    }

    /**
     * Local implementation for deactivate
     * Returns success without actually deactivating
     */
    private async localDeactivate(): Promise<void> {
        console.log('Local deactivate: mock deactivation')
        // Does nothing in local mode
    }

    private openLocalQrScannerSimulator(
        signal?: AbortSignal
    ): Promise<QrScannerSimulatorResult> {
        const defaultResult: QrScannerSimulatorResult = {
            action: 'closed',
            reason: 'QR scanner simulator was closed'
        }

        if (typeof window === 'undefined') {
            return Promise.resolve(defaultResult)
        }

        const simulationId = this.createTraceId('qr-scanner-sim')
        const popup = window.open(
            '',
            `vcash-qr-scanner-simulator-${simulationId}`,
            'width=460,height=620,menubar=no,toolbar=no,location=no,status=no,resizable=yes'
        )

        if (!popup) {
            return Promise.resolve({
                ...defaultResult,
                reason: 'QR scanner simulator popup was blocked'
            })
        }

        popup.document.write(`<!doctype html>
<html lang="en">
<head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>VCash QR Scanner Simulator</title>
    <style>
        * { box-sizing: border-box; }
        body {
            margin: 0;
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background:
                radial-gradient(circle at top, rgba(96, 165, 250, 0.18), transparent 35%),
                #111827;
            font-family: Arial, sans-serif;
            color: #f9fafb;
        }
        .scanner {
            width: 400px;
            border-radius: 28px;
            padding: 24px;
            background: linear-gradient(180deg, #2f3a4a, #111827);
            box-shadow: 0 24px 70px rgba(0, 0, 0, 0.5);
            border: 2px solid #4b5563;
        }
        .brand {
            font-size: 13px;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: #9ca3af;
            margin-bottom: 18px;
        }
        .viewport {
            position: relative;
            border-radius: 18px;
            padding: 18px;
            background: #0f172a;
            border: 4px solid #374151;
            margin-bottom: 18px;
            overflow: hidden;
        }
        .viewport::before,
        .viewport::after {
            content: "";
            position: absolute;
            width: 28px;
            height: 28px;
            border: 3px solid #22c55e;
        }
        .viewport::before {
            top: 12px;
            left: 12px;
            border-right: 0;
            border-bottom: 0;
        }
        .viewport::after {
            bottom: 12px;
            right: 12px;
            border-left: 0;
            border-top: 0;
        }
        .scan-line {
            position: absolute;
            left: 18px;
            right: 18px;
            height: 2px;
            background: linear-gradient(90deg, transparent, #22c55e, transparent);
            box-shadow: 0 0 12px #22c55e;
            animation: scan 2.2s ease-in-out infinite;
        }
        .label {
            font-size: 14px;
            color: #d1d5db;
            margin-bottom: 8px;
        }
        textarea {
            width: 100%;
            min-height: 120px;
            resize: vertical;
            border-radius: 12px;
            border: 1px solid #4b5563;
            background: #1f2937;
            color: #f9fafb;
            padding: 12px;
            font: 14px/1.4 Arial, sans-serif;
        }
        textarea:focus {
            outline: 2px solid #22c55e;
            outline-offset: 1px;
        }
        .hint {
            margin-top: 8px;
            font-size: 12px;
            color: #9ca3af;
        }
        .actions {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 14px;
            margin-top: 20px;
        }
        button {
            border: 0;
            border-radius: 14px;
            padding: 16px 12px;
            color: white;
            font-weight: 800;
            font-size: 15px;
            cursor: pointer;
        }
        .fail { background: #dc2626; }
        .submit { background: #16a34a; }
        .status {
            margin-top: 16px;
            min-height: 20px;
            text-align: center;
            color: #d1d5db;
            font-size: 13px;
        }
        @keyframes scan {
            0%, 100% { top: 24px; opacity: 0.35; }
            50% { top: calc(100% - 24px); opacity: 1; }
        }
    </style>
</head>
<body>
    <main class="scanner">
        <div class="brand">VCash QR Scanner</div>
        <section class="viewport" aria-label="Scanner viewport">
            <div class="scan-line" aria-hidden="true"></div>
        </section>
        <label class="label" for="scanResult">Scan result</label>
        <textarea id="scanResult" placeholder="https://market.vcash.rs?code=123-456-789"></textarea>
        <div class="hint">Enter the decoded QR content, or click Timeout to simulate a scan timeout.</div>
        <div class="actions">
            <button class="fail" type="button" id="timeout">TIMEOUT</button>
            <button class="submit" type="button" id="submit">SUBMIT</button>
        </div>
        <div class="status" id="status">Waiting for scan result...</div>
    </main>
    <script>
        const simulationId = ${JSON.stringify(simulationId)};
        const status = document.getElementById('status');
        const scanResult = document.getElementById('scanResult');
        function complete(action) {
            const content = scanResult.value.trim();
            if (action === 'success' && !content) {
                status.textContent = 'Enter a scan result before submitting.';
                scanResult.focus();
                return;
            }
            status.textContent = action === 'success'
                ? 'Scan successful. Sending response...'
                : 'Scan timed out. Sending response...';
            window.opener.postMessage({
                source: 'vcash-qr-scanner-simulator',
                simulationId,
                action,
                content
            }, '*');
            window.setTimeout(() => window.close(), 350);
        }
        document.getElementById('submit').addEventListener('click', () => complete('success'));
        document.getElementById('timeout').addEventListener('click', () => complete('timeout'));
    </script>
</body>
</html>`)
        popup.document.close()
        popup.focus()

        return new Promise((resolve) => {
            let isResolved = false
            const cleanup = () => {
                isResolved = true
                window.removeEventListener('message', handleMessage)
                window.clearInterval(closeWatcher)
                signal?.removeEventListener('abort', onAbort)
            }
            const resolveOnce = (result: QrScannerSimulatorResult) => {
                if (isResolved) {
                    return
                }
                cleanup()
                resolve(result)
            }
            const onAbort = () => {
                if (!popup.closed) {
                    popup.close()
                }
                resolveOnce({
                    action: 'closed',
                    reason: 'QR scan aborted'
                })
            }
            const handleMessage = (event: MessageEvent) => {
                const data = event.data as {
                    source?: string
                    simulationId?: string
                    action?: string
                    content?: string
                }

                if (
                    data?.source !== 'vcash-qr-scanner-simulator' ||
                    data.simulationId !== simulationId
                ) {
                    return
                }

                if (data.action === 'success') {
                    resolveOnce({
                        action: 'success',
                        content: data.content
                    })
                    return
                }

                // Only an explicit Timeout click is a retryable scan timeout.
                // Closing the window / aborting must not look like a timeout.
                if (data.action === 'timeout' || data.action === 'fail') {
                    resolveOnce({
                        action: 'timeout',
                        reason: 'Scan timeout - simulated by operator'
                    })
                    return
                }
            }
            const closeWatcher = window.setInterval(() => {
                if (popup.closed) {
                    resolveOnce(defaultResult)
                }
            }, 500)

            window.addEventListener('message', handleMessage)

            if (signal?.aborted) {
                onAbort()
                return
            }
            signal?.addEventListener('abort', onAbort)
        })
    }

    /**
     * Local implementation for QR scanner
     * Opens a visual QR scanner simulator for development.
     * The popup waits indefinitely until the operator clicks Submit or Timeout.
     */
    private async localStartQrScanner(signal?: AbortSignal): Promise<string> {
        console.log('Local startQrScanner: opening simulator')

        if (signal?.aborted) {
            throw new Error('QR scan aborted')
        }

        const result = await this.openLocalQrScannerSimulator(signal)

        if (signal?.aborted || result.reason === 'QR scan aborted') {
            throw new Error('QR scan aborted')
        }

        if (result.action === 'success') {
            return result.content ?? ''
        }

        // Explicit Timeout button in the simulator — callers may retry.
        if (result.action === 'timeout') {
            throw new QrScannerTimeoutError(
                result.reason ?? 'Scan timeout - no QR code detected'
            )
        }

        // Window closed, popup blocked, etc. — stop without treating as timeout.
        throw new Error(result.reason ?? 'QR scanner stopped')
    }

    /**
     * Local implementation for POS status
     * Returns mock status data for development
     */
    private async localGetPosStatus(): Promise<PosStatusResponse> {
        console.log('Local getPosStatus: returning mock status')
        return {
            success: true,
            message: null,
            errorCode: null,
            device: 'pos',
            device_type: 'hardware',
            data: {
                status: 'ready',
                terminalId: 'POS001',
                configuration: {
                    timeout: 30,
                    currency: 'RSD'
                }
            },
            timestamp: new Date().toISOString()
        }
    }

    private openLocalPosSimulator(
        request: PosActivateRequest
    ): Promise<PosSimulatorResult> {
        const defaultResult: PosSimulatorResult = {
            action: 'decline',
            cardType: 'VISA',
            reason: 'POS simulator was closed'
        }

        if (typeof window === 'undefined') {
            return Promise.resolve(defaultResult)
        }

        const simulationId = this.createTraceId('pos-sim')
        const amount = Number(request.amount)
        const formattedAmount = new Intl.NumberFormat('sr-RS', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(Number.isFinite(amount) ? amount : 0)
        const popup = window.open(
            '',
            `vcash-pos-simulator-${simulationId}`,
            'width=420,height=680,menubar=no,toolbar=no,location=no,status=no,resizable=no'
        )

        if (!popup) {
            return Promise.resolve({
                ...defaultResult,
                reason: 'POS simulator popup was blocked'
            })
        }

        popup.document.write(`<!doctype html>
<html lang="en">
<head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>VCash POS Simulator</title>
    <style>
        * { box-sizing: border-box; }
        body {
            margin: 0;
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #111827;
            font-family: Arial, sans-serif;
            color: #f9fafb;
        }
        .terminal {
            width: 360px;
            border-radius: 28px;
            padding: 24px;
            background: linear-gradient(180deg, #2f3a4a, #111827);
            box-shadow: 0 24px 70px rgba(0, 0, 0, 0.5);
            border: 2px solid #4b5563;
        }
        .screen {
            border-radius: 18px;
            padding: 22px;
            background: #d1fae5;
            color: #064e3b;
            min-height: 230px;
            border: 4px solid #0f172a;
        }
        .brand {
            font-size: 13px;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: #6b7280;
            margin-bottom: 22px;
        }
        .label {
            font-size: 14px;
            color: #047857;
            margin-bottom: 8px;
        }
        .amount {
            font-size: 40px;
            font-weight: 800;
            line-height: 1;
            margin-bottom: 6px;
        }
        .currency {
            font-size: 16px;
            font-weight: 700;
            color: #047857;
            margin-bottom: 24px;
        }
        .cards {
            display: grid;
            gap: 10px;
        }
        label {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 10px 12px;
            border-radius: 10px;
            background: rgba(255, 255, 255, 0.55);
            color: #064e3b;
            font-weight: 700;
        }
        input { transform: scale(1.2); }
        .keypad {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
            margin: 22px 0;
        }
        .key {
            height: 42px;
            border-radius: 10px;
            background: #374151;
            border: 1px solid #6b7280;
            box-shadow: inset 0 -2px 0 rgba(0, 0, 0, 0.25);
        }
        .actions {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 14px;
        }
        button {
            border: 0;
            border-radius: 14px;
            padding: 16px 12px;
            color: white;
            font-weight: 800;
            font-size: 16px;
            cursor: pointer;
        }
        .accept { background: #16a34a; }
        .decline { background: #dc2626; }
        .status {
            margin-top: 16px;
            min-height: 20px;
            text-align: center;
            color: #d1d5db;
            font-size: 13px;
        }
    </style>
</head>
<body>
    <main class="terminal">
        <section class="screen">
            <div class="brand">VCash POS</div>
            <div class="label">Amount due</div>
            <div class="amount">${formattedAmount}</div>
            <div class="currency">RSD</div>
            <div class="cards">
                <label><input type="radio" name="cardType" value="VISA" checked /> VISA</label>
                <label><input type="radio" name="cardType" value="Mastercard" /> Mastercard</label>
            </div>
        </section>
        <div class="keypad" aria-hidden="true">
            <div class="key"></div><div class="key"></div><div class="key"></div>
            <div class="key"></div><div class="key"></div><div class="key"></div>
            <div class="key"></div><div class="key"></div><div class="key"></div>
        </div>
        <div class="actions">
            <button class="decline" type="button" id="decline">DECLINE</button>
            <button class="accept" type="button" id="accept">ACCEPT</button>
        </div>
        <div class="status" id="status">Waiting for customer card...</div>
    </main>
    <script>
        const simulationId = ${JSON.stringify(simulationId)};
        const status = document.getElementById('status');
        function selectedCardType() {
            return document.querySelector('input[name="cardType"]:checked').value;
        }
        function complete(action) {
            status.textContent = action === 'accept' ? 'Approved. Sending response...' : 'Declined. Sending response...';
            window.opener.postMessage({
                source: 'vcash-pos-simulator',
                simulationId,
                action,
                cardType: selectedCardType()
            }, '*');
            window.setTimeout(() => window.close(), 350);
        }
        document.getElementById('accept').addEventListener('click', () => complete('accept'));
        document.getElementById('decline').addEventListener('click', () => complete('decline'));
    </script>
</body>
</html>`)
        popup.document.close()
        popup.focus()

        return new Promise((resolve) => {
            let isResolved = false
            const cleanup = () => {
                isResolved = true
                window.removeEventListener('message', handleMessage)
                window.clearInterval(closeWatcher)
            }
            const resolveOnce = (result: PosSimulatorResult) => {
                if (isResolved) {
                    return
                }
                cleanup()
                resolve(result)
            }
            const handleMessage = (event: MessageEvent) => {
                const data = event.data as {
                    source?: string
                    simulationId?: string
                    action?: string
                    cardType?: string
                }

                if (
                    data?.source !== 'vcash-pos-simulator' ||
                    data.simulationId !== simulationId
                ) {
                    return
                }

                resolveOnce({
                    action: data.action === 'accept' ? 'accept' : 'decline',
                    cardType:
                        data.cardType === 'Mastercard' ? 'Mastercard' : 'VISA'
                })
            }
            const closeWatcher = window.setInterval(() => {
                if (popup.closed) {
                    resolveOnce(defaultResult)
                }
            }, 500)

            window.addEventListener('message', handleMessage)
        })
    }

    /**
     * Local implementation for POS activate
     * Returns mock successful transaction data for development.
     *
     * Mirrors the real card-payment lifecycle on the backend:
     *   1. Create an INITIALIZED draft deposit with the initial terminal
     *      request payload.
     *   2. Open the POS simulator and wait for the user to accept / decline.
     *   3. Confirm the draft deposit to PENDING (on accept) or FAILED (on
     *      decline), forwarding the simulated terminal response as the
     *      confirm payload so the backend can store the full attempt history.
     */
    private async localActivatePos(
        request: PosActivateRequest
    ): Promise<PosActivateResponse> {
        console.log('Local activatePos: mock activation', request)

        const amountNumber = Number(request.amount)
        const safeAmount = Number.isFinite(amountNumber) ? amountNumber : 0
        const requestedAt = new Date().toISOString()

        const requestPayload = {
            step: 'request',
            terminalId: request.terminalId,
            amount: safeAmount,
            currency: request.currency,
            voucherTypeId: request.voucher_type_id,
            depositTypeId: request.depositTypeId,
            requestedAt
        }

        // Step 1: create the deposit as INITIALIZED before the simulator opens
        // so the backend has a record of the attempt even if the user walks
        // away from the simulator.
        let deviceDraftDepositId: string
        try {
            const draftDepositResponse =
                await TransactionService.CreateDeviceDraftDeposit({
                    amount: safeAmount,
                    voucherTypeId: request.voucher_type_id,
                    depositTypeId: request.depositTypeId,
                    status: 'INITIALIZED',
                    payload: requestPayload
                })
            deviceDraftDepositId =
                draftDepositResponse.deviceDraftDeposit.deviceDraftDepositId
        } catch (error) {
            console.error('Local activatePos draft deposit create error:', error)
            return {
                success: false,
                message: `Failed to create card draft deposit: ${error instanceof Error ? error.message : 'Unknown error'}`,
                errorCode: 'DRAFT_DEPOSIT_ERROR',
                device: 'pos',
                device_type: 'hardware',
                data: {},
                timestamp: new Date().toISOString()
            }
        }

        const simulatorResult = await this.openLocalPosSimulator(request)

        if (simulatorResult.action === 'decline') {
            const respondedAt = new Date().toISOString()
            const declineMessage =
                simulatorResult.reason ?? 'Card declined by simulator'
            const declinePayload = {
                step: 'declined',
                terminalId: `KLA${request.terminalId}`,
                cardIssuer: simulatorResult.cardType,
                transactionStatus: 'Declined',
                responseCode: '05',
                responseMessage: declineMessage,
                amount: safeAmount.toFixed(2),
                currency: request.currency,
                respondedAt
            }

            // Step 2b: mark the deposit FAILED so it does not count against the
            // device's pending-draft cap and the failure response is stored.
            try {
                await TransactionService.ConfirmDeviceDraftDeposit({
                    deviceDraftDepositId,
                    status: 'FAILED',
                    payload: declinePayload
                })
            } catch (error) {
                // We intentionally swallow this error: the user already saw the
                // decline in the simulator, and the INITIALIZED draft will be
                // cleaned up by the backend's normal expiry. Logging is enough.
                console.error(
                    'Local activatePos draft deposit confirm (FAILED) error:',
                    error
                )
            }

            return {
                success: false,
                message: declineMessage,
                errorCode: 'CARD_DECLINED',
                device: 'pos',
                device_type: 'hardware',
                data: {
                    TerminalID: `KLA${request.terminalId}`,
                    MidNumber: '223456789012345',
                    CardIssuer: simulatorResult.cardType,
                    CardApplicationName:
                        simulatorResult.cardType === 'Mastercard'
                            ? 'Mastercard'
                            : 'Visa Debit',
                    CardNumber:
                        simulatorResult.cardType === 'Mastercard'
                            ? '5412********3456'
                            : '4532********9012',
                    TransactionStatus: 'Declined',
                    ResponseCode: '05',
                    TransactionDate: new Date()
                        .toLocaleDateString('sr-RS', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                        })
                        .replace(/\s/g, ''),
                    TransactionTime: new Date().toLocaleTimeString('en-GB', {
                        hour12: false
                    })
                },
                timestamp: respondedAt
            }
        }

        const transactionNumber = Math.floor(
            Math.random() * 900000 + 100000
        ).toString()
        const invoiceNumber = Math.floor(
            Math.random() * 900000 + 100000
        ).toString()
        const respondedAt = new Date().toISOString()
        const approvalCode = Math.floor(Math.random() * 900000 + 100000)
            .toString()
            .padStart(6, '0')
        const rrn = Math.floor(Math.random() * 1e12)
            .toString()
            .padStart(12, '0')
        const emvData = `ARQC (80) ${Math.floor(Math.random() * 1e16)
            .toString(16)
            .toUpperCase()}`
        const responseData = {
            TerminalID: `KLA${request.terminalId}`,
            MidNumber: '223456789012345',
            CardIssuer: simulatorResult.cardType,
            AID:
                simulatorResult.cardType === 'Mastercard'
                    ? 'A0000000041010'
                    : 'A0000000031010',
            CardApplicationName:
                simulatorResult.cardType === 'Mastercard'
                    ? 'Mastercard'
                    : 'Visa Debit',
            CardNumber:
                simulatorResult.cardType === 'Mastercard'
                    ? '5412********3456'
                    : '4532********9012',
            TransactionType: '01',
            TransactionFlag: request.terminalId,
            InvoiceNumber: invoiceNumber,
            TransactionDate: new Date()
                .toLocaleDateString('en-GB', {
                    year: '2-digit',
                    month: '2-digit',
                    day: '2-digit'
                })
                .replace(/\//g, ''),
            TransactionTime: new Date()
                .toLocaleTimeString('en-GB', { hour12: false })
                .replace(/:/g, ''),
            RRN: rrn,
            ApprovalCode: approvalCode,
            ResponseCode: '00',
            TransactionStatus: 'Approved',
            Amount: safeAmount.toFixed(2),
            EMVData: emvData,
            InstallmentsNumber: '',
            SignatureLine: '1'
        }

        const approvedPayload = {
            step: 'approved',
            transactionNumber,
            invoiceNumber,
            approvalCode,
            rrn,
            responseCode: '00',
            transactionStatus: 'Approved',
            terminal: responseData,
            respondedAt
        }

        // Step 2a: transition the draft to PENDING so it counts as a real draft
        // and the voucher flow can pick it up.
        try {
            await TransactionService.ConfirmDeviceDraftDeposit({
                deviceDraftDepositId,
                status: 'PENDING',
                payload: approvedPayload
            })
        } catch (error) {
            console.error(
                'Local activatePos draft deposit confirm (PENDING) error:',
                error
            )
            return {
                success: false,
                message: `Failed to confirm card draft deposit: ${error instanceof Error ? error.message : 'Unknown error'}`,
                errorCode: 'DRAFT_DEPOSIT_ERROR',
                device: 'pos',
                device_type: 'hardware',
                data: {},
                timestamp: new Date().toISOString()
            }
        }

        return {
            success: true,
            message: 'Activate successful',
            errorCode: null,
            device: 'pos',
            device_type: 'hardware',
            sequenceNumber: '0001',
            transactionNumber,
            deviceDraftDepositId,
            amount: request.amount,
            terminalId: request.terminalId,
            traceId: crypto.randomUUID(),
            data: responseData,
            timestamp: respondedAt
        }
    }

    private ensureIcuSimulatorMessageHandler(): void {
        if (this.icuSimulatorMessageHandler || typeof window === 'undefined') {
            return
        }

        this.icuSimulatorMessageHandler = (event: MessageEvent) => {
            const data = event.data as {
                source?: string
                simulationId?: string
                type?: string
                streamUrl?: string
                deviceId?: string
                traceId?: string
                success?: boolean
                age?: number
                gender?: 'male' | 'female'
                cameraIndex?: number
                cameraName?: string
                faceImage?: string
                sessionDuration?: number
                errorCode?: ICUCameraError['code']
                errorMessage?: string
                reason?: string
            }

            if (
                data?.source !== 'vcash-icu-camera-simulator' ||
                !data.simulationId ||
                data.simulationId !== this.icuSimulatorId
            ) {
                return
            }

            if (data.type === 'ready' && data.streamUrl) {
                this.icuSimulatorStreamUrl = data.streamUrl
                this.icuSimulatorActivated = true
                if (data.deviceId) {
                    this.icuSimulatorDeviceId = data.deviceId
                }
                return
            }

            if (data.type === 'deactivated') {
                this.icuSimulatorActivated = false
                this.icuSimulatorStreamUrl = null
                return
            }

            if (data.type === 'verify-age-response' && data.traceId) {
                const resolver = this.icuVerifyResolvers.get(data.traceId)
                if (!resolver) {
                    return
                }
                this.icuVerifyResolvers.delete(data.traceId)
                resolver({
                    success: data.success === true,
                    age: data.age,
                    gender: data.gender,
                    cameraIndex: data.cameraIndex,
                    cameraName: data.cameraName,
                    faceImage: data.faceImage,
                    sessionDuration: data.sessionDuration,
                    errorCode: data.errorCode,
                    errorMessage: data.errorMessage,
                    reason: data.reason
                })
            }
        }

        window.addEventListener('message', this.icuSimulatorMessageHandler)
    }

    private resetIcuSimulatorState(): void {
        if (this.icuSimulatorId && typeof window !== 'undefined') {
            delete window.__vcashLocalIcuStreams?.[this.icuSimulatorId]
        }
        this.icuSimulatorPopup = null
        this.icuSimulatorId = null
        this.icuSimulatorStreamUrl = null
        this.icuSimulatorActivated = false
        this.icuSimulatorDeviceId = 'local-icu-camera'
        this.icuSimulatorReadyPromise = null
        for (const resolver of this.icuVerifyResolvers.values()) {
            resolver({
                success: false,
                errorCode: 'CAMERA_OFFLINE',
                errorMessage: 'ICU camera simulator was closed',
                reason: 'ICU camera simulator was closed'
            })
        }
        this.icuVerifyResolvers.clear()
    }

    private postToIcuSimulator(message: Record<string, unknown>): void {
        if (this.icuSimulatorPopup && !this.icuSimulatorPopup.closed) {
            this.icuSimulatorPopup.postMessage(message, '*')
        }
    }

    private createIcuSimulatorReadyPromise(
        simulationId: string,
        popup: Window
    ): Promise<string> {
        const readyPromise = new Promise<string>((resolve, reject) => {
            let settled = false
            const settle = (handler: () => void) => {
                if (settled) return
                settled = true
                window.clearInterval(closeWatcher)
                window.removeEventListener('message', onReady)
                handler()
            }

            const onReady = (event: MessageEvent) => {
                const data = event.data as {
                    source?: string
                    simulationId?: string
                    type?: string
                    streamUrl?: string
                }
                if (
                    data?.source !== 'vcash-icu-camera-simulator' ||
                    data.simulationId !== simulationId ||
                    data.type !== 'ready'
                ) {
                    return
                }
                if (!data.streamUrl) {
                    this.icuSimulatorReadyPromise = null
                    settle(() =>
                        reject(
                            new Error(
                                'Webcam unavailable — allow camera access in the simulator'
                            )
                        )
                    )
                    return
                }
                this.icuSimulatorStreamUrl = data.streamUrl
                this.icuSimulatorActivated = true
                this.icuSimulatorReadyPromise = null
                settle(() => resolve(data.streamUrl!))
            }

            const closeWatcher = window.setInterval(() => {
                if (popup.closed) {
                    this.resetIcuSimulatorState()
                    settle(() =>
                        reject(new Error('ICU camera simulator was closed'))
                    )
                }
            }, 400)

            window.addEventListener('message', onReady)
        })

        this.icuSimulatorReadyPromise = readyPromise
        return readyPromise
    }

    private openLocalIcuCameraSimulator(): Promise<string> {
        if (typeof window === 'undefined') {
            return Promise.reject(
                new Error('ICU camera simulator requires a browser window')
            )
        }

        this.ensureIcuSimulatorMessageHandler()

        if (
            this.icuSimulatorPopup &&
            !this.icuSimulatorPopup.closed &&
            this.icuSimulatorStreamUrl
        ) {
            this.icuSimulatorPopup.focus()
            return Promise.resolve(this.icuSimulatorStreamUrl)
        }

        if (this.icuSimulatorReadyPromise) {
            return this.icuSimulatorReadyPromise
        }

        if (
            this.icuSimulatorPopup &&
            !this.icuSimulatorPopup.closed &&
            this.icuSimulatorId
        ) {
            const simulationId = this.icuSimulatorId
            const popup = this.icuSimulatorPopup
            const readyPromise = this.createIcuSimulatorReadyPromise(
                simulationId,
                popup
            )
            this.postToIcuSimulator({
                source: 'vcash-icu-camera-host',
                simulationId,
                type: 'activate-request'
            })
            popup.focus()
            return readyPromise
        }

        if (this.icuSimulatorPopup && !this.icuSimulatorPopup.closed) {
            this.icuSimulatorPopup.close()
        }

        const simulationId = this.createTraceId('icu-camera-sim')
        this.icuSimulatorId = simulationId
        this.icuSimulatorStreamUrl = null
        this.icuSimulatorActivated = false
        this.icuSimulatorPopup = null

        const popup = window.open(
            '',
            'vcash-icu-camera-simulator',
            'popup=yes,width=560,height=920,menubar=no,toolbar=no,location=no,status=no,resizable=yes'
        )

        if (!popup) {
            this.icuSimulatorReadyPromise = null
            return Promise.reject(
                new Error('ICU camera simulator popup was blocked')
            )
        }

        this.icuSimulatorPopup = popup
        const readyPromise = this.createIcuSimulatorReadyPromise(
            simulationId,
            popup
        )

        popup.document.open()
        popup.document.write(`<!doctype html>
<html lang="en">
<head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>VCash ICU Camera Simulator</title>
    <style>
        * { box-sizing: border-box; }
        body {
            margin: 0;
            min-height: 100vh;
            background:
                radial-gradient(circle at 15% 10%, rgba(59, 130, 246, 0.28), transparent 32%),
                radial-gradient(circle at 85% 0%, rgba(139, 92, 246, 0.22), transparent 28%),
                linear-gradient(180deg, #0b1220 0%, #111827 55%, #0f172a 100%);
            font-family: "Segoe UI", Arial, sans-serif;
            color: #f8fafc;
        }
        .app {
            width: min(520px, 96vw);
            margin: 0 auto;
            padding: 18px 16px 28px;
        }
        .hero {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 14px;
        }
        .brand {
            font-size: 12px;
            letter-spacing: 0.16em;
            text-transform: uppercase;
            color: #93c5fd;
            font-weight: 700;
        }
        .badge {
            padding: 6px 10px;
            border-radius: 999px;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            background: rgba(34, 197, 94, 0.18);
            color: #86efac;
            border: 1px solid rgba(34, 197, 94, 0.35);
        }
        .badge.offline {
            background: rgba(239, 68, 68, 0.16);
            color: #fca5a5;
            border-color: rgba(239, 68, 68, 0.35);
        }
        .panel {
            border-radius: 20px;
            border: 1px solid rgba(148, 163, 184, 0.25);
            background: rgba(15, 23, 42, 0.72);
            backdrop-filter: blur(8px);
            box-shadow: 0 18px 50px rgba(0, 0, 0, 0.35);
            padding: 14px;
            margin-bottom: 14px;
        }
        .panel h2 {
            margin: 0 0 10px;
            font-size: 14px;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: #bfdbfe;
        }
        .viewport {
            position: relative;
            border-radius: 16px;
            overflow: hidden;
            background: #020617;
            border: 2px solid rgba(59, 130, 246, 0.45);
            aspect-ratio: 4 / 3;
        }
        video {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
            transform: scaleX(-1);
        }
        .overlay {
            position: absolute;
            inset: 0;
            pointer-events: none;
        }
        .corner {
            position: absolute;
            width: 34px;
            height: 34px;
            border: 3px solid #60a5fa;
            filter: drop-shadow(0 0 8px rgba(96, 165, 250, 0.8));
        }
        .corner.tl { top: 14px; left: 14px; border-right: 0; border-bottom: 0; }
        .corner.tr { top: 14px; right: 14px; border-left: 0; border-bottom: 0; }
        .corner.bl { bottom: 14px; left: 14px; border-right: 0; border-top: 0; }
        .corner.br { bottom: 14px; right: 14px; border-left: 0; border-top: 0; }
        .oval {
            position: absolute;
            left: 50%;
            top: 50%;
            width: 52%;
            height: 68%;
            transform: translate(-50%, -50%);
            border: 2px dashed rgba(147, 197, 253, 0.85);
            border-radius: 50%;
            box-shadow: 0 0 24px rgba(59, 130, 246, 0.35);
        }
        .scan-line {
            position: absolute;
            left: 8%;
            right: 8%;
            height: 2px;
            background: linear-gradient(90deg, transparent, #38bdf8, transparent);
            box-shadow: 0 0 14px #38bdf8;
            animation: scan 2.4s ease-in-out infinite;
        }
        .stream-meta {
            margin-top: 10px;
            display: grid;
            gap: 6px;
            font-size: 12px;
            color: #cbd5e1;
        }
        .stream-url {
            word-break: break-all;
            padding: 8px 10px;
            border-radius: 10px;
            background: rgba(2, 6, 23, 0.65);
            border: 1px solid rgba(100, 116, 139, 0.45);
            font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
            font-size: 11px;
        }
        .grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
        }
        label {
            display: block;
            font-size: 12px;
            color: #cbd5e1;
            margin-bottom: 6px;
        }
        input, select {
            width: 100%;
            border-radius: 10px;
            border: 1px solid rgba(100, 116, 139, 0.55);
            background: rgba(2, 6, 23, 0.75);
            color: #f8fafc;
            padding: 10px 11px;
            font-size: 14px;
        }
        input:focus, select:focus {
            outline: 2px solid rgba(59, 130, 246, 0.65);
            outline-offset: 1px;
        }
        .actions {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            margin-top: 12px;
        }
        button {
            border: 0;
            border-radius: 12px;
            padding: 13px 10px;
            font-size: 14px;
            font-weight: 800;
            letter-spacing: 0.04em;
            cursor: pointer;
            color: white;
        }
        .success { background: linear-gradient(180deg, #22c55e, #15803d); }
        .fail { background: linear-gradient(180deg, #ef4444, #b91c1c); }
        .ghost {
            background: rgba(30, 41, 59, 0.9);
            border: 1px solid rgba(100, 116, 139, 0.55);
            margin-top: 8px;
            width: 100%;
        }
        .request-banner {
            display: none;
            margin-bottom: 10px;
            padding: 10px 12px;
            border-radius: 12px;
            border: 1px solid rgba(250, 204, 21, 0.45);
            background: rgba(234, 179, 8, 0.14);
            color: #fde68a;
            font-size: 13px;
            animation: pulseBanner 1.2s ease-in-out infinite;
        }
        .request-banner.active { display: block; }
        .log {
            min-height: 72px;
            max-height: 120px;
            overflow: auto;
            padding: 8px 10px;
            border-radius: 10px;
            background: rgba(2, 6, 23, 0.65);
            border: 1px solid rgba(100, 116, 139, 0.45);
            font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
            font-size: 11px;
            color: #cbd5e1;
            white-space: pre-wrap;
        }
        .hint {
            margin-top: 8px;
            font-size: 11px;
            color: #94a3b8;
            line-height: 1.45;
        }
        @keyframes scan {
            0%, 100% { top: 16%; opacity: 0.35; }
            50% { top: 78%; opacity: 1; }
        }
        @keyframes pulseBanner {
            0%, 100% { box-shadow: 0 0 0 rgba(250, 204, 21, 0); }
            50% { box-shadow: 0 0 16px rgba(250, 204, 21, 0.35); }
        }
    </style>
</head>
<body>
    <main class="app">
        <header class="hero">
            <div class="brand">VCash ICU Camera</div>
            <div class="badge" id="statusBadge">Starting</div>
        </header>

        <section class="panel">
            <h2>Live Stream</h2>
            <div class="viewport">
                <video id="cameraFeed" autoplay playsinline muted></video>
                <div class="overlay">
                    <span class="corner tl"></span>
                    <span class="corner tr"></span>
                    <span class="corner bl"></span>
                    <span class="corner br"></span>
                    <span class="oval"></span>
                    <span class="scan-line"></span>
                </div>
            </div>
            <div class="stream-meta">
                <div>Device: <strong id="deviceName">local-webcam</strong></div>
                <div>Stream URL</div>
                <div class="stream-url" id="streamUrl">Waiting for camera...</div>
            </div>
        </section>

        <section class="panel">
            <h2>Verify Age Response</h2>
            <div class="request-banner" id="requestBanner">
                Incoming verify-age request — customize and submit a response.
            </div>
            <div class="grid">
                <div>
                    <label for="age">Detected age</label>
                    <input id="age" type="number" min="1" max="99" value="25" />
                </div>
                <div>
                    <label for="gender">Gender</label>
                    <select id="gender">
                        <option value="male">male</option>
                        <option value="female">female</option>
                    </select>
                </div>
                <div>
                    <label for="cameraIndex">Camera index</label>
                    <input id="cameraIndex" type="number" min="0" value="0" />
                </div>
                <div>
                    <label for="sessionDuration">Session duration (ms)</label>
                    <input id="sessionDuration" type="number" min="0" value="1200" />
                </div>
            </div>
            <div style="margin-top:10px">
                <label for="failureCode">Failure error code</label>
                <select id="failureCode">
                    <option value="NO_FACE_DETECTED">NO_FACE_DETECTED</option>
                    <option value="TIMEOUT">TIMEOUT</option>
                    <option value="CAMERA_NOT_READY">CAMERA_NOT_READY</option>
                    <option value="CAMERA_ERROR">CAMERA_ERROR</option>
                    <option value="CAMERA_OFFLINE">CAMERA_OFFLINE</option>
                    <option value="INTERNAL_ERROR">INTERNAL_ERROR</option>
                </select>
            </div>
            <div class="actions">
                <button class="success" type="button" id="submitSuccess">SUBMIT SUCCESS</button>
                <button class="fail" type="button" id="submitFail">SUBMIT FAILURE</button>
            </div>
            <button class="ghost" type="button" id="deactivate">DEACTIVATE CAMERA</button>
            <p class="hint">Success captures the current webcam frame as faceImage. Keep this window open while testing age verification in the main app.</p>
        </section>

        <section class="panel">
            <h2>Event Log</h2>
            <div class="log" id="eventLog">Booting simulator...</div>
        </section>
    </main>
    <script>
        (function () {
        const simulationId = ${JSON.stringify(simulationId)};
        const statusBadge = document.getElementById('statusBadge');
        const streamUrlEl = document.getElementById('streamUrl');
        const eventLog = document.getElementById('eventLog');
        const requestBanner = document.getElementById('requestBanner');
        const video = document.getElementById('cameraFeed');
        let mediaStream = null;
        let streamUrl = null;
        let pendingVerifyTraceId = null;
        let pendingVerifyTimeout = null;
        let activated = false;

        function appendLog(message) {
            const stamp = new Date().toLocaleTimeString();
            eventLog.textContent = '[' + stamp + '] ' + message + '\\n' + eventLog.textContent;
        }

        function setStatus(text, online) {
            statusBadge.textContent = text;
            statusBadge.classList.toggle('offline', !online);
        }

        function captureFaceImage() {
            if (!video.videoWidth || !video.videoHeight) {
                return '';
            }
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) return '';
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            return canvas.toDataURL('image/jpeg', 0.82);
        }

        function readForm() {
            return {
                age: Number(document.getElementById('age').value) || 25,
                gender: document.getElementById('gender').value === 'female' ? 'female' : 'male',
                cameraIndex: Number(document.getElementById('cameraIndex').value) || 0,
                cameraName: document.getElementById('deviceName').textContent || 'local-webcam',
                sessionDuration: Number(document.getElementById('sessionDuration').value) || 1200,
                failureCode: document.getElementById('failureCode').value
            };
        }

        function completeVerify(success) {
            if (!pendingVerifyTraceId) {
                appendLog('No pending verify-age request. Use Begin Detection in the app first.');
                return;
            }

            const form = readForm();
            const traceId = pendingVerifyTraceId;
            pendingVerifyTraceId = null;
            requestBanner.classList.remove('active');
            if (pendingVerifyTimeout) {
                window.clearTimeout(pendingVerifyTimeout);
                pendingVerifyTimeout = null;
            }

            if (success) {
                appendLog('Sending verify-age SUCCESS for ' + traceId);
                window.opener.postMessage({
                    source: 'vcash-icu-camera-simulator',
                    simulationId,
                    type: 'verify-age-response',
                    traceId,
                    success: true,
                    age: form.age,
                    gender: form.gender,
                    cameraIndex: form.cameraIndex,
                    cameraName: form.cameraName,
                    faceImage: captureFaceImage(),
                    sessionDuration: form.sessionDuration
                }, '*');
            } else {
                appendLog('Sending verify-age FAILURE (' + form.failureCode + ') for ' + traceId);
                window.opener.postMessage({
                    source: 'vcash-icu-camera-simulator',
                    simulationId,
                    type: 'verify-age-response',
                    traceId,
                    success: false,
                    errorCode: form.failureCode,
                    errorMessage: 'Simulated ' + form.failureCode,
                    reason: 'Simulated ' + form.failureCode
                }, '*');
            }
        }

        async function bootCamera() {
            if (mediaStream) {
                mediaStream.getTracks().forEach((track) => track.stop());
            }
            if (streamUrl && window.opener && window.opener.__vcashLocalIcuStreams) {
                delete window.opener.__vcashLocalIcuStreams[simulationId];
            }
            streamUrl = null;
            try {
                mediaStream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
                    audio: false
                });
                video.srcObject = mediaStream;
                await video.play();
                var registry = window.opener.__vcashLocalIcuStreams = window.opener.__vcashLocalIcuStreams || {};
                registry[simulationId] = mediaStream;
                streamUrl = 'local-media-stream:' + simulationId;
                streamUrlEl.textContent = streamUrl;
                activated = true;
                setStatus('Live', true);
                appendLog('Camera started. Stream URL registered with main app.');
                window.opener.postMessage({
                    source: 'vcash-icu-camera-simulator',
                    simulationId,
                    type: 'ready',
                    streamUrl,
                    deviceId: 'local-icu-camera'
                }, '*');
            } catch (error) {
                setStatus('Camera Error', false);
                appendLog('Camera error: ' + (error && error.message ? error.message : 'Unknown error'));
                window.opener.postMessage({
                    source: 'vcash-icu-camera-simulator',
                    simulationId,
                    type: 'ready',
                    streamUrl: '',
                    deviceId: 'local-icu-camera'
                }, '*');
            }
        }

        function deactivateCamera() {
            if (mediaStream) {
                mediaStream.getTracks().forEach((track) => track.stop());
            }
            if (window.opener && window.opener.__vcashLocalIcuStreams) {
                delete window.opener.__vcashLocalIcuStreams[simulationId];
            }
            mediaStream = null;
            streamUrl = null;
            activated = false;
            video.srcObject = null;
            streamUrlEl.textContent = 'Camera deactivated';
            setStatus('Offline', false);
            appendLog('Camera deactivated.');
            window.opener.postMessage({
                source: 'vcash-icu-camera-simulator',
                simulationId,
                type: 'deactivated'
            }, '*');
        }

        window.addEventListener('message', (event) => {
            const data = event.data || {};
            if (data.source !== 'vcash-icu-camera-host' || data.simulationId !== simulationId) {
                return;
            }

            if (data.type === 'verify-age-request') {
                pendingVerifyTraceId = data.traceId;
                requestBanner.classList.add('active');
                appendLog('Verify-age request received: ' + data.traceId);
                if (typeof data.timeout === 'number' && data.timeout > 0) {
                    if (pendingVerifyTimeout) window.clearTimeout(pendingVerifyTimeout);
                    pendingVerifyTimeout = window.setTimeout(() => {
                        if (pendingVerifyTraceId !== data.traceId) return;
                        document.getElementById('failureCode').value = 'TIMEOUT';
                        completeVerify(false);
                    }, data.timeout);
                }
            }

            if (data.type === 'deactivate-request') {
                deactivateCamera();
            }

            if (data.type === 'activate-request') {
                appendLog('Camera re-activation requested.');
                void bootCamera();
            }
        });

        document.getElementById('submitSuccess').addEventListener('click', () => completeVerify(true));
        document.getElementById('submitFail').addEventListener('click', () => completeVerify(false));
        document.getElementById('deactivate').addEventListener('click', deactivateCamera);
        window.addEventListener('beforeunload', deactivateCamera);

        void bootCamera();
        })();
    </script>
</body>
</html>`)
            popup.document.close()
            popup.focus()

        return readyPromise
    }

    private waitForIcuVerifyAgeResponse(
        traceId: string,
        timeout?: number
    ): Promise<IcuCameraVerifySimulatorResult> {
        const defaultFailure = (): IcuCameraVerifySimulatorResult => ({
            success: false,
            errorCode: 'TIMEOUT',
            errorMessage: 'Age verification timed out in simulator',
            reason: 'Age verification timed out in simulator'
        })

        return new Promise((resolve) => {
            let settled = false
            const settle = (result: IcuCameraVerifySimulatorResult) => {
                if (settled) return
                settled = true
                window.clearTimeout(timeoutHandle)
                this.icuVerifyResolvers.delete(traceId)
                resolve(result)
            }

            this.icuVerifyResolvers.set(traceId, settle)

            this.postToIcuSimulator({
                source: 'vcash-icu-camera-host',
                simulationId: this.icuSimulatorId,
                type: 'verify-age-request',
                traceId,
                timeout
            })

            const timeoutMs =
                typeof timeout === 'number' && timeout > 0 ? timeout + 500 : 60000
            const timeoutHandle = window.setTimeout(() => {
                settle(defaultFailure())
            }, timeoutMs)
        })
    }

    private async localActivateICUCamera(
        traceId?: string
    ): Promise<ICUCameraActivateResponse> {
        const resolvedTraceId = traceId ?? this.createTraceId('cam-act')
        try {
            const streamUrl = await this.openLocalIcuCameraSimulator()
            if (!streamUrl) {
                return {
                    success: false,
                    data: null,
                    message: 'Camera simulator failed to start webcam feed',
                    error: {
                        code: 'CAMERA_NOT_READY',
                        message: 'No stream URL from simulator',
                        category: 'hardware'
                    },
                    timestamp: new Date().toISOString()
                }
            }

            return {
                success: true,
                data: {
                    activated: true,
                    streamUrl,
                    deviceId: this.icuSimulatorDeviceId,
                    deviceType: 'local-simulator',
                    traceId: resolvedTraceId,
                    timestamp: new Date().toISOString()
                },
                message: 'Camera activated successfully (local simulator)',
                error: null,
                timestamp: new Date().toISOString()
            }
        } catch (error) {
            return {
                success: false,
                data: null,
                message: `Failed to activate camera simulator: ${error instanceof Error ? error.message : 'Unknown error'}`,
                error: {
                    code: 'CAMERA_ERROR',
                    message: 'Local ICU camera simulator error',
                    category: 'hardware'
                },
                timestamp: new Date().toISOString()
            }
        }
    }

    private async localVerifyAgeWithICUCamera(
        timeout?: number,
        traceId?: string
    ): Promise<ICUCameraVerifyAgeResponse> {
        const resolvedTraceId = traceId ?? this.createTraceId('cam-verify')
        const startedAt = Date.now()

        try {
            await this.openLocalIcuCameraSimulator()
        } catch (error) {
            return {
                success: false,
                data: null,
                message: `Camera simulator unavailable: ${error instanceof Error ? error.message : 'Unknown error'}`,
                error: {
                    code: 'CAMERA_OFFLINE',
                    message: 'ICU camera simulator is not available',
                    category: 'hardware'
                },
                timestamp: new Date().toISOString()
            }
        }

        const simulatorResult = await this.waitForIcuVerifyAgeResponse(
            resolvedTraceId,
            timeout
        )

        if (simulatorResult.success) {
            return {
                success: true,
                data: {
                    age: simulatorResult.age ?? 25,
                    gender: simulatorResult.gender ?? 'male',
                    cameraIndex: simulatorResult.cameraIndex ?? 0,
                    cameraName:
                        simulatorResult.cameraName ?? 'local-webcam',
                    faceImage: simulatorResult.faceImage ?? '',
                    sessionDuration:
                        simulatorResult.sessionDuration ??
                        Date.now() - startedAt,
                    traceId: resolvedTraceId,
                    timestamp: new Date().toISOString()
                },
                message: 'Age verification successful (local simulator)',
                error: null,
                timestamp: new Date().toISOString()
            }
        }

        const errorCode = simulatorResult.errorCode ?? 'NO_FACE_DETECTED'
        const errorCategoryMap: Record<
            ICUCameraError['code'],
            ICUCameraError['category']
        > = {
            HTTP_ERROR: 'network',
            INVALID_RESPONSE: 'unknown',
            ALREADY_ACTIVATED: 'hardware',
            TIMEOUT: 'timeout',
            NO_FACE_DETECTED: 'no_face',
            CAMERA_NOT_READY: 'hardware',
            NETWORK_ERROR: 'network',
            AUTH_ERROR: 'auth',
            CAMERA_ERROR: 'hardware',
            CAMERA_OFFLINE: 'hardware',
            INVALID_PARAMETER: 'validation',
            INTERNAL_ERROR: 'unknown',
            STATUS_ERROR: 'unknown',
            SERVICE_DISABLED: 'unknown'
        }

        return {
            success: false,
            data: null,
            message:
                simulatorResult.errorMessage ??
                simulatorResult.reason ??
                'Age verification failed (local simulator)',
            error: {
                code: errorCode,
                message:
                    simulatorResult.errorMessage ??
                    simulatorResult.reason ??
                    'Simulated verification failure',
                category: errorCategoryMap[errorCode] ?? 'unknown'
            },
            timestamp: new Date().toISOString()
        }
    }

    private async localGetICUCameraStatus(
        _traceId?: string
    ): Promise<ICUCameraStatusResponse> {
        const popupOpen =
            this.icuSimulatorPopup !== null && !this.icuSimulatorPopup.closed

        return {
            success: true,
            data: {
                activated: this.icuSimulatorActivated,
                connected: popupOpen,
                streamActive:
                    this.icuSimulatorActivated &&
                    Boolean(this.icuSimulatorStreamUrl),
                streamUrl: this.icuSimulatorStreamUrl,
                deviceInfo: popupOpen
                    ? {
                          deviceId: this.icuSimulatorDeviceId,
                          deviceName: 'local-webcam',
                          deviceType: 'local-simulator'
                      }
                    : null
            },
            message: 'Camera status retrieved (local simulator)',
            error: null,
            timestamp: new Date().toISOString()
        }
    }

    private async localDeactivateICUCamera(
        traceId?: string
    ): Promise<ICUCameraDeactivateResponse> {
        const resolvedTraceId = traceId ?? this.createTraceId('cam-deact')

        this.postToIcuSimulator({
            source: 'vcash-icu-camera-host',
            simulationId: this.icuSimulatorId,
            type: 'deactivate-request',
            traceId: resolvedTraceId
        })

        if (this.icuSimulatorId && typeof window !== 'undefined') {
            delete window.__vcashLocalIcuStreams?.[this.icuSimulatorId]
        }

        this.icuSimulatorActivated = false
        this.icuSimulatorStreamUrl = null

        return {
            success: true,
            data: {
                activated: false,
                traceId: resolvedTraceId,
                timestamp: new Date().toISOString()
            },
            message: 'Camera deactivated successfully (local simulator)',
            error: null,
            timestamp: new Date().toISOString()
        }
    }

    // Public API methods that route to appropriate implementation

    async print(imageUrl: string, sessionId?: string): Promise<ApiResponse> {
        if (this.isHttpMode()) {
            return this.httpPrint(imageUrl, sessionId)
        }
        return this.localPrint(imageUrl)
    }

    async printSlip(
        slip: PrintSlipRequest,
        sessionId?: string
    ): Promise<ApiResponse> {
        if (this.isHttpMode()) {
            return this.httpPrintSlip(slip, sessionId)
        }
        return this.localPrintSlip(slip)
    }

    async activate(
        jwt: string,
        voucherTypeId: string,
        sessionId?: string
    ): Promise<ActivateApiResponse> {
        if (this.isHttpMode()) {
            return this.httpActivate(
                jwt,
                voucherTypeId === 'betting' ? '30' : '20',
                sessionId
            )
        }
        return this.localActivate(jwt)
    }

    async deactivate(sessionId?: string): Promise<void> {
        if (this.isHttpMode()) {
            return this.httpDeactivate(sessionId)
        }
        return this.localDeactivate()
    }

    async saveDeviceToken(token: string, sessionId?: string): Promise<boolean> {
        if (this.isHttpMode()) {
            return this.httpSaveDeviceToken(token, sessionId)
        }
        return this.localSaveDeviceToken(token)
    }

    async getDeviceToken(sessionId?: string): Promise<string> {
        if (this.isHttpMode()) {
            return this.httpGetDeviceToken(sessionId)
        }
        return this.localGetDeviceToken()
    }

    async sendLog(
        level: string,
        message: string,
        sessionId?: string
    ): Promise<void> {
        if (this.isHttpMode()) {
            return this.httpSendLog(level, message, sessionId)
        }
        return this.localSendLog(level, message)
    }

    async getCredentials(
        sessionId?: string
    ): Promise<DeviceCredentials | null> {
        if (this.isHttpMode()) {
            return this.httpGetCredentials(sessionId)
        }
        return this.localGetCredentials()
    }

    async startQrScanner(
        signal?: AbortSignal,
        sessionId?: string
    ): Promise<string | undefined> {
        // Hardware scans return 408 after ~20s with no QR; keep listening until
        // a code is found or the caller aborts. In local mode the simulator
        // waits indefinitely and only throws QrScannerTimeoutError when the
        // operator clicks Timeout — that case is safe to retry.
        while (signal?.aborted === false) {
            try {
                if (this.isHttpMode()) {
                    return await this.httpStartQrScanner(signal, sessionId)
                } else {
                    return await this.localStartQrScanner(signal)
                }
            } catch (error) {
                if (error instanceof QrScannerTimeoutError) {
                    console.log('QR scan timeout, retrying...')
                    continue
                }
                throw error
            }
        }
    }

    async stopQrScanner(sessionId?: string): Promise<void> {
        if (this.isHttpMode()) {
            return this.httpStopQrScanner(sessionId)
        }
        console.log('stop mock scan')
        return Promise.resolve()
    }

    async getPosStatus(sessionId?: string): Promise<PosStatusResponse> {
        if (this.isHttpMode()) {
            return this.httpGetPosStatus(sessionId)
        }
        return this.localGetPosStatus()
    }

    async activatePos(
        request: PosActivateRequest,
        sessionId?: string
    ): Promise<PosActivateResponse> {
        const response = this.isHttpMode()
            ? await this.httpActivatePos(request, sessionId)
            : await this.localActivatePos(request)

        // The payment slip is no longer auto-printed here. The UI renders the
        // `pos_slip` template after activation (on success and failure) and
        // lets the user print it manually via the regular print endpoint.
        return response
    }

    private async deviceSessionRequest(
        operation: 'start' | 'end',
        sessionId: string
    ): Promise<DeviceSessionResponse> {
        if (!sessionId.trim()) throw new Error('Device session ID is required')
        if (!this.isHttpMode()) {
            // Local camera simulation never creates a hardware recording.
            return {
                success: true,
                data: { sessionId, inProgress: operation === 'start' }
            }
        }

        const response = await fetch(
            `${this.baseUrl}/api/v1/session/${operation}`,
            {
                method: 'POST',
                headers: { 'X-Session-ID': sessionId },
                // Allow the final stop request to survive page navigation.
                keepalive: operation === 'end',
                signal: AbortSignal.timeout(10000)
            }
        )
        if (!response.ok) {
            throw new Error(`Device session ${operation}: HTTP ${response.status}`)
        }
        const result: DeviceSessionResponse = await response.json()
        if (
            result?.success !== true ||
            result.data?.inProgress !== (operation === 'start') ||
            (operation === 'start' &&
                (typeof result.data.sessionId !== 'string' ||
                    !result.data.sessionId.trim()))
        ) {
            throw new Error(
                `Device session ${operation}: invalid or failed response`
            )
        }
        return result
    }

    startDeviceSession(sessionId: string): Promise<DeviceSessionResponse> {
        return this.deviceSessionRequest('start', sessionId)
    }

    endDeviceSession(sessionId: string): Promise<DeviceSessionResponse> {
        return this.deviceSessionRequest('end', sessionId)
    }

    async activateICUCamera(
        traceId?: string
    ): Promise<ICUCameraActivateResponse> {
        const response = this.isHttpMode()
            ? await this.httpIcuRequest<ICUCameraActivateData>('activate')
            : await this.localActivateICUCamera(traceId)
        reportIcuResponse('activate', response, traceId)
        return response
    }

    async verifyAgeWithICUCamera(
        timeout?: number,
        traceId?: string,
        signal?: AbortSignal
    ): Promise<ICUCameraVerifyAgeResponse> {
        const response = this.isHttpMode()
            ? await this.httpIcuRequest<ICUCameraVerifyAgeData>(
                  'verify-age',
                  signal,
                  timeout === undefined ? {} : { timeout }
              )
            : await this.localVerifyAgeWithICUCamera(timeout, traceId)
        reportIcuResponse('verify-age', response, traceId, signal?.aborted)
        return response
    }

    async getICUCameraStatus(
        traceId?: string
    ): Promise<ICUCameraStatusResponse> {
        const response = this.isHttpMode()
            ? await this.httpIcuRequest<ICUCameraStatusData>('status')
            : await this.localGetICUCameraStatus()
        reportIcuResponse('status', response, traceId)
        return response
    }

    async deactivateICUCamera(
        traceId?: string
    ): Promise<ICUCameraDeactivateResponse> {
        const response = this.isHttpMode()
            ? await this.httpIcuRequest<ICUCameraDeactivateData>('deactivate')
            : await this.localDeactivateICUCamera(traceId)
        reportIcuResponse('deactivate', response, traceId)
        return response
    }
}

// Create singleton instance
export const apiService = new ApiService()

// Development helper - expose to window for debugging
if (import.meta.env.DEV) {
    ;(window as unknown as { apiService: ApiService }).apiService = apiService
}

// Export types for external use
export type {
    ActivateApiResponse,
    ApiMode,
    ApiResponse,
    DeviceCredentials,
    ICUCameraActivateResponse,
    ICUCameraDeactivateResponse,
    ICUCameraError,
    ICUCameraStatusResponse,
    ICUCameraVerifyAgeResponse,
    PosActivateRequest,
    PosActivateResponse,
    PosStatusResponse,
    PrintSlipRequest
}
export { QrScannerTimeoutError }
